import { ref } from 'vue'

// Global confirmation modal state
const isModalVisible = ref(false)
const modalConfig = ref({
  title: '',
  message: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  allowOutsideClick: false,
  type: 'info' // 'info' | 'danger' | 'warning' | 'success'
})

let resolvePromise = null
let rejectPromise = null

export function useConfirmation() {
  const showConfirmation = (titleOrConfig, message, confirmText, cancelText) => {
    return new Promise((resolve, reject) => {
      // Support both object config and individual parameters
      let config
      if (typeof titleOrConfig === 'object') {
        config = titleOrConfig
      } else {
        config = {
          title: titleOrConfig,
          message: message,
          confirmText: confirmText,
          cancelText: cancelText
        }
      }
      
      modalConfig.value = {
        title: config.title || 'Confirm Action',
        message: config.message || '',
        confirmText: config.confirmText || 'Confirm',
        cancelText: config.cancelText || 'Cancel',
        allowOutsideClick: config.allowOutsideClick ?? false,
        type: config.type || 'info'
      }
      
      isModalVisible.value = true
      resolvePromise = resolve
      rejectPromise = reject
    })
  }

  const handleConfirm = () => {
    isModalVisible.value = false
    if (resolvePromise) {
      resolvePromise(true)
      resolvePromise = null
      rejectPromise = null
    }
  }

  const handleCancel = () => {
    isModalVisible.value = false
    if (rejectPromise) {
      rejectPromise(false)
      resolvePromise = null
      rejectPromise = null
    }
  }

  // Convenience methods for common use cases
  const confirmDelete = (itemName = 'this item') => {
    return showConfirmation({
      title: 'Delete Confirmation',
      message: `Are you sure you want to delete ${itemName}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    })
  }

  const confirmAction = (title, message, options = {}) => {
    return showConfirmation({
      title: title || 'Confirm Action',
      message: message || '',
      confirmText: options.confirmText || 'Confirm',
      cancelText: options.cancelText || 'Cancel',
      type: options.type || 'info',
      allowOutsideClick: options.allowOutsideClick ?? true
    })
  }

  const confirmWarning = (title, message, options = {}) => {
    return showConfirmation({
      title: title || 'Warning',
      message: message || 'Are you sure you want to proceed?',
      confirmText: options.confirmText || 'Proceed',
      cancelText: options.cancelText || 'Cancel',
      type: 'warning',
      allowOutsideClick: options.allowOutsideClick ?? false
    })
  }

  const confirmDanger = (title, message, options = {}) => {
    return showConfirmation({
      title: title || 'Danger',
      message: message || 'This action may have serious consequences.',
      confirmText: options.confirmText || 'Proceed',
      cancelText: options.cancelText || 'Cancel',
      type: 'danger',
      allowOutsideClick: options.allowOutsideClick ?? false
    })
  }

  const confirmCustomAction = (actionName, description = '') => {
    return showConfirmation({
      title: `Confirm ${actionName}`,
      message: description || `Are you sure you want to ${actionName.toLowerCase()}?`,
      confirmText: actionName,
      cancelText: 'Cancel',
      type: 'info'
    })
  }

  const confirmCritical = (title, message) => {
    return showConfirmation({
      title: title || 'Critical Action Required',
      message: message || 'This action requires confirmation to proceed.',
      confirmText: 'Proceed',
      cancelText: 'Cancel',
      allowOutsideClick: false,
      type: 'danger'
    })
  }

  return {
    // State
    isModalVisible,
    modalConfig,
    
    // Actions
    showConfirmation,
    handleConfirm,
    handleCancel,
    
    // Convenience methods
    confirmDelete,
    confirmAction,
    confirmWarning,
    confirmDanger,
    confirmCustomAction,
    confirmCritical
  }
}
